import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, Tabs, Table, Tag, Modal, Button as AntButton } from "antd";
import {
  DollarSign,
  Users,
  UserCheck,
  UserX,
  Plus,
  ClipboardList,
  Edit,
  Check,
} from "lucide-react";
import { StatCard } from "@/components/dashboard/StatCard";

const tiers = [
  {
    id: 1,
    name: "Platinum",
    price: 50000,
    color: "from-primary to-primary/80",
    benefits: [
      "Premium booth location",
      "Logo on all marketing materials",
      "Speaking opportunity",
      "VIP event tickets (10)",
      "Dedicated social media posts",
      "Press release mention",
    ],
  },
  {
    id: 2,
    name: "Gold",
    price: 25000,
    color: "from-warning to-warning/80",
    benefits: [
      "Standard booth location",
      "Logo on website",
      "VIP event tickets (5)",
      "Social media mentions",
      "Newsletter feature",
    ],
  },
  {
    id: 3,
    name: "Silver",
    price: 10000,
    color: "from-secondary to-muted",
    benefits: [
      "Logo on website",
      "VIP event tickets (2)",
      "Social media mention",
      "Program listing",
    ],
  },
];

const mockSponsors = [
  {
    id: 1,
    name: "Tech Corp",
    tier: "Platinum",
    status: "active" as const,
    logo: "TC",
  },
  {
    id: 2,
    name: "Innovation Inc",
    tier: "Gold",
    status: "active" as const,
    logo: "II",
  },
  {
    id: 3,
    name: "Global Solutions",
    tier: "Silver",
    status: "active" as const,
    logo: "GS",
  },
  {
    id: 4,
    name: "Digital Ventures",
    tier: "Gold",
    status: "inactive" as const,
    logo: "DV",
  },
];

const mockPackages = [
  {
    id: 1,
    name: "Annual Platinum Package",
    price: 50000,
    dueDate: "2024-12-31",
    deliveredOn: "2024-01-15",
    quantity: 3,
    benefits: "All Platinum Benefits",
  },
  {
    id: 2,
    name: "Q1 Gold Sponsorship",
    price: 25000,
    dueDate: "2024-03-31",
    deliveredOn: "2024-01-20",
    quantity: 5,
    benefits: "All Gold Benefits",
  },
  {
    id: 3,
    name: "Event Silver Package",
    price: 10000,
    dueDate: "2024-06-30",
    deliveredOn: null,
    quantity: 8,
    benefits: "All Silver Benefits",
  },
];

// Simple TierCard component
const TierCard = ({ id, name, price, color, benefits, onEdit }: any) => {
  return (
    <Card className="relative overflow-hidden">
      <div className={`bg-gradient-to-br ${color} -m-6 mb-4 h-1`} />
      <div className="flex items-center justify-between">
        <h3 className="text-xl font-bold">{name}</h3>
        <AntButton
          type="text"
          icon={<Edit size={16} />}
          onClick={() => onEdit?.(id)}
          className="flex-shrink-0"
        />
      </div>
      <p
        className={`mt-2 inline-flex items-center rounded-full border border-transparent bg-gradient-to-r ${color ?? "from-primary to-primary/80"} px-2.5 py-0.5 text-2xl text-xs font-semibold text-primary-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2`}
      >
        ${price.toLocaleString()}
      </p>
      <ul className="mt-4 space-y-2">
        {benefits.map((benefit: string) => (
          <li
            key={""}
            className="flex items-center gap-2 text-sm text-foreground"
          >
            <Check size={16} className="text-success flex-shrink-0" />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>
    </Card>
  );
};

// Simple SponsorCard component
const SponsorCard = ({ name, tier, status, logo }: any) => {
  return (
    <Card
      className="transition-all hover:shadow-lg"
      hoverable
      bodyStyle={{ padding: "20px" }}
    >
      {/* Top Section */}
      <div className="mb-4 flex items-center gap-3">
        <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-primary/10 text-lg font-bold text-primary">
          {logo}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="mb-1 truncate text-base font-semibold text-foreground">
            {name}
          </h3>
          <Tag className="rounded-full border-0 bg-muted px-3 py-0.5 text-xs font-medium text-muted-foreground">
            {tier}
          </Tag>
        </div>
      </div>

      {/* Bottom Section - Status */}
      <div className="flex items-center justify-between border-t border-border pt-3">
        <span className="text-sm text-muted-foreground">Status</span>
        <Tag
          color={status === "active" ? "blue" : "default"}
          className="rounded-full border-0 px-3 py-1 text-xs font-medium"
        >
          {status}
        </Tag>
      </div>
    </Card>
  );
};

// Simple PackagesTable component
const PackagesTable = ({ packages }: { packages: any[] }) => {
  const columns = [
    {
      title: "Package Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Price",
      dataIndex: "price",
      key: "price",
      render: (price: number) => `$${price.toLocaleString()}`,
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
    },
    {
      title: "Delivered On",
      dataIndex: "deliveredOn",
      key: "deliveredOn",
      render: (date: string | null) => date ?? "Pending",
    },
    {
      title: "Quantity",
      dataIndex: "quantity",
      key: "quantity",
    },
    {
      title: "Benefits",
      dataIndex: "benefits",
      key: "benefits",
    },
  ];

  return <Table dataSource={packages} columns={columns} rowKey="id" />;
};

const Sponsorships = () => {
  const [createSponsorOpen, setCreateSponsorOpen] = useState(false);
  const [createPackageOpen, setCreatePackageOpen] = useState(false);
  const [editTierOpen, setEditTierOpen] = useState(false);
  const [logBenefitsOpen, setLogBenefitsOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<number | null>(null);

  const handleEditTier = (tierId: number) => {
    setSelectedTier(tierId);
    setEditTierOpen(true);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold sm:text-3xl">Sponsorships</h1>
          <p className="mt-1 text-sm text-muted-foreground sm:text-base">
            Manage sponsors, packages, and benefits
          </p>
        </div>
        <div className="flex flex-wrap gap-2 sm:gap-3">
          <Button
            variant="outline"
            onClick={() => setLogBenefitsOpen(true)}
            className="flex-1 sm:flex-initial"
          >
            <ClipboardList className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Log Benefits</span>
            <span className="sm:hidden">Log</span>
          </Button>
          <Button
            variant="outline"
            onClick={() => setCreatePackageOpen(true)}
            className="flex-1 sm:flex-initial"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">New Package</span>
            <span className="sm:hidden">Package</span>
          </Button>
          <Button
            onClick={() => setCreateSponsorOpen(true)}
            className="flex-1 sm:flex-initial"
          >
            <Plus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Create Sponsor</span>
            <span className="sm:hidden">Sponsor</span>
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sponsors (This Year)"
          value="47"
          change="+8"
          changeType="increase"
          icon={Users}
          iconColor="text-primary"
        />
        <StatCard
          title="Sponsor Revenue"
          value="$485,000"
          change="+23%"
          changeType="increase"
          icon={DollarSign}
          iconColor="text-success"
        />
        <StatCard
          title="Active Sponsors"
          value="42"
          change="+5"
          changeType="increase"
          icon={UserCheck}
          iconColor="text-info"
        />
        <StatCard
          title="Inactive Sponsors"
          value="5"
          change="-2"
          changeType="decrease"
          icon={UserX}
          iconColor="text-muted-foreground"
        />
      </div>

      {/* Sponsorship Tiers */}
      <div>
        <h2 className="mb-4 text-lg font-semibold sm:text-xl">
          Sponsorship Tiers
        </h2>
        <div className="grid gap-4 sm:grid-cols-2 sm:gap-6 lg:grid-cols-3">
          {tiers.map(tier => (
            <TierCard key={tier.id} {...tier} onEdit={handleEditTier} />
          ))}
        </div>
      </div>

      {/* Tabs for Sponsors and Packages */}
      <div>
        <Tabs
          defaultActiveKey="sponsors"
          items={[
            {
              key: "sponsors",
              label: "Sponsor Accounts",
              children: (
                <div>
                  <h2 className="mb-4 text-lg font-semibold sm:text-xl">
                    Sponsor Accounts
                  </h2>
                  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                    {mockSponsors.map(sponsor => (
                      <SponsorCard key={sponsor.id} {...sponsor} />
                    ))}
                  </div>
                </div>
              ),
            },
            {
              key: "packages",
              label: "Sponsor Packages",
              children: <PackagesTable packages={mockPackages} />,
            },
          ]}
        />
      </div>

      {/* Modals */}
      <Modal
        title="Create Sponsor"
        open={createSponsorOpen}
        onCancel={() => setCreateSponsorOpen(false)}
        onOk={() => setCreateSponsorOpen(false)}
        footer={null}
      >
        <p>Create Sponsor form will be implemented here.</p>
      </Modal>
      <Modal
        title="Create Package"
        open={createPackageOpen}
        onCancel={() => setCreatePackageOpen(false)}
        onOk={() => setCreatePackageOpen(false)}
        footer={null}
      >
        <p>Create Package form will be implemented here.</p>
      </Modal>
      <Modal
        title="Edit Tier"
        open={editTierOpen}
        onCancel={() => setEditTierOpen(false)}
        onOk={() => setEditTierOpen(false)}
        footer={null}
      >
        <p>Edit Tier form will be implemented here. Tier ID: {selectedTier}</p>
      </Modal>
      <Modal
        title="Log Benefits"
        open={logBenefitsOpen}
        onCancel={() => setLogBenefitsOpen(false)}
        onOk={() => setLogBenefitsOpen(false)}
        footer={null}
      >
        <p>Log Benefits form will be implemented here.</p>
      </Modal>
    </div>
  );
};

export default Sponsorships;
